import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Testimonial {
  id: number;
  nameKey: string;
  roleKey: string;
  textKey: string;
  country: string;
  flag: string;
  amount: string;
  profit: string;
  initials: string;
  color: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    nameKey: 'testimonials.t1.name',
    roleKey: 'testimonials.t1.role',
    textKey: 'testimonials.t1.text',
    country: 'Deutschland',
    flag: '🇩🇪',
    amount: '€15,000',
    profit: '+€5,550',
    initials: 'MK',
    color: '#c8a84b',
  },
  {
    id: 2,
    nameKey: 'testimonials.t2.name',
    roleKey: 'testimonials.t2.role',
    textKey: 'testimonials.t2.text',
    country: 'България',
    flag: '🇧🇬',
    amount: '€8,000',
    profit: '+€2,320',
    initials: 'ИД',
    color: '#a0522d',
  },
  {
    id: 3,
    nameKey: 'testimonials.t3.name',
    roleKey: 'testimonials.t3.role',
    textKey: 'testimonials.t3.text',
    country: 'Polska',
    flag: '🇵🇱',
    amount: '€12,000',
    profit: '+€4,440',
    initials: 'PW',
    color: '#8b1a1a',
  },
  {
    id: 4,
    nameKey: 'testimonials.t4.name',
    roleKey: 'testimonials.t4.role',
    textKey: 'testimonials.t4.text',
    country: 'Italia',
    flag: '🇮🇹',
    amount: '€20,000',
    profit: '+€7,400',
    initials: 'LR',
    color: '#2d6a2d',
  },
  {
    id: 5,
    nameKey: 'testimonials.t5.name',
    roleKey: 'testimonials.t5.role',
    textKey: 'testimonials.t5.text',
    country: 'Norge',
    flag: '🇳🇴',
    amount: '€10,000',
    profit: '+€3,700',
    initials: 'EH',
    color: '#1a3a6b',
  },
];

const StarRating = () => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <div key={i} className="w-4 h-4 flex items-center justify-center">
        <i className="ri-star-fill text-[#d4af37] text-sm" />
      </div>
    ))}
  </div>
);

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (idx: number) => {
    if (animating || idx === active) return;
    setAnimating(true);
    setTimeout(() => {
      setActive(idx);
      setAnimating(false);
    }, 300);
  };

  const next = () => goTo((active + 1) % testimonials.length);
  const prev = () => goTo((active - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActive(a => (a + 1) % testimonials.length);
    }, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActive(a => (a + 1) % testimonials.length);
    }, 5000);
  };

  const handleGoTo = (idx: number) => { goTo(idx); resetInterval(); };
  const handleNext  = () => { next(); resetInterval(); };
  const handlePrev  = () => { prev(); resetInterval(); };

  const item = testimonials[active];

  return (
    <section id="testimonials" className="py-20 md:py-28 relative overflow-hidden" style={{ background: '#0a0a0a' }}>
      {/* Фоновый паттерн */}
      <div className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(212,175,55,0.3) 40px, rgba(212,175,55,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(212,175,55,0.3) 40px, rgba(212,175,55,0.3) 41px)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">

        {/* Заголовок */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-5">
            <div className="w-1.5 h-1.5 bg-[#d4af37] rounded-full" />
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium" style={{ fontFamily: "'Inter',sans-serif" }}>
              {t('testimonials.badge')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase mb-3" style={{ fontFamily: "'Oswald',sans-serif" }}>
            {t('testimonials.title1')}{' '}
            <span className="text-[#d4af37]">{t('testimonials.title2')}</span>
          </h2>
          <p className="text-white/50 text-sm md:text-base max-w-xl mx-auto" style={{ fontFamily: "'Inter',sans-serif" }}>
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Основной блок */}
        <div className="flex flex-col lg:flex-row gap-8 items-stretch">

          {/* Левая панель — список стран */}
          <div className="flex flex-row lg:flex-col gap-2 lg:w-56 flex-shrink-0 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {testimonials.map((t2, idx) => (
              <button
                key={t2.id}
                onClick={() => handleGoTo(idx)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  idx === active
                    ? 'bg-[#d4af37]/15 border-[#d4af37]/50'
                    : 'bg-white/3 border-white/10 hover:border-white/25'
                }`}
              >
                <span className="text-xl">{t2.flag}</span>
                <div className="text-left">
                  <div className={`text-xs font-semibold tracking-wide ${idx === active ? 'text-[#d4af37]' : 'text-white/60'}`}
                    style={{ fontFamily: "'Inter',sans-serif" }}>
                    {t2.country}
                  </div>
                  <div className="text-white/35 text-xs" style={{ fontFamily: "'Inter',sans-serif" }}>
                    {t2.profit}
                  </div>
                </div>
                {idx === active && (
                  <div className="ml-auto w-1 h-1 bg-[#d4af37] rounded-full flex-shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Правая панель — отзыв */}
          <div className="flex-1">
            <div
              className="relative rounded-2xl border border-white/10 p-6 md:p-10 h-full flex flex-col justify-between transition-opacity duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                opacity: animating ? 0 : 1,
              }}
            >
              {/* Угловой акцент */}
              <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none overflow-hidden rounded-tr-2xl">
                <div className="absolute top-0 right-0 w-24 h-24"
                  style={{ background: `linear-gradient(225deg, ${item.color}22 0%, transparent 60%)` }} />
              </div>

              {/* Верх: аватар + имя + рейтинг */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-8">
                {/* Аватар без лица */}
                <div className="flex-shrink-0">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white font-black text-xl relative"
                    style={{ background: `linear-gradient(135deg, ${item.color}cc, ${item.color}55)`, fontFamily: "'Oswald',sans-serif" }}
                  >
                    {item.initials}
                    {/* Флаг-бейдж */}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#111] border border-white/20 flex items-center justify-center text-xs">
                      {item.flag}
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-1">
                    <span className="text-white font-bold text-base" style={{ fontFamily: "'Oswald',sans-serif" }}>
                      {t(item.nameKey)}
                    </span>
                    <span className="text-white/30 text-xs">·</span>
                    <span className="text-white/50 text-xs" style={{ fontFamily: "'Inter',sans-serif" }}>
                      {item.country}
                    </span>
                  </div>
                  <div className="text-white/40 text-xs mb-2" style={{ fontFamily: "'Inter',sans-serif" }}>
                    {t(item.roleKey)}
                  </div>
                  <StarRating />
                </div>

                {/* Результат */}
                <div className="flex-shrink-0 text-right">
                  <div className="text-[#d4af37] font-black text-2xl" style={{ fontFamily: "'Oswald',sans-serif" }}>
                    {item.profit}
                  </div>
                  <div className="text-white/40 text-xs" style={{ fontFamily: "'Inter',sans-serif" }}>
                    {t('testimonials.from')} {item.amount}
                  </div>
                </div>
              </div>

              {/* Цитата */}
              <div className="relative mb-8">
                <div className="absolute -top-2 -left-1 text-[#d4af37]/20 text-7xl leading-none select-none" style={{ fontFamily: 'Georgia, serif' }}>&ldquo;</div>
                <p className="text-white/80 text-base md:text-lg leading-relaxed pl-6 relative z-10" style={{ fontFamily: "'Inter',sans-serif" }}>
                  {t(item.textKey)}
                </p>
              </div>

              {/* Нижняя панель: навигация */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {testimonials.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleGoTo(idx)}
                      className={`transition-all duration-200 rounded-full cursor-pointer ${
                        idx === active ? 'w-6 h-2 bg-[#d4af37]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrev}
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/50 hover:border-[#d4af37]/50 hover:text-[#d4af37] transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-sm" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-white/50 hover:border-[#d4af37]/50 hover:text-[#d4af37] transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Нижняя строка — суммарная статистика */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { val: '2,400+', labelKey: 'testimonials.stat1' },
            { val: '€48M+',  labelKey: 'testimonials.stat2' },
            { val: '37%',    labelKey: 'testimonials.stat3' },
            { val: '18',     labelKey: 'testimonials.stat4' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-white/8 p-4 text-center" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <div className="text-[#d4af37] font-black text-2xl mb-1" style={{ fontFamily: "'Oswald',sans-serif" }}>{s.val}</div>
              <div className="text-white/40 text-xs tracking-wide" style={{ fontFamily: "'Inter',sans-serif" }}>{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
